import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ManualReportForm } from '../components/reports/ManualReportForm';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

type Brand = {
  id: string;
  name: string;
};

export default function AdminManualReportBuilder() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialData, setInitialData] = useState<any>(null);

  useEffect(() => {
    loadBrands();
    if (isEdit && id) {
      loadReport();
    }
  }, [id, isEdit]);

  async function loadBrands() {
    try {
      const res = await fetch('/api/admin/brands', {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setBrands(data);
      }
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  }

  async function loadReport() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/reports/${id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const report = await res.json();
        // Parse reportData JSON if it exists
        if (report.reportData) {
          try {
            const parsed = JSON.parse(report.reportData);
            setInitialData({
              ...parsed,
              campaignName: report.campaignName,
              brandId: report.brandId,
              reportDate: report.reportDate?.split('T')[0] || '',
              campaignStartDate: report.campaignStartDate?.split('T')[0] || '',
              campaignEndDate: report.campaignEndDate?.split('T')[0] || '',
            });
          } catch (e) {
            console.error('Failed to parse reportData:', e);
          }
        } else {
          setInitialData({
            campaignName: report.campaignName,
            brandId: report.brandId,
            reportDate: report.reportDate?.split('T')[0] || '',
            campaignStartDate: report.campaignStartDate?.split('T')[0] || '',
            campaignEndDate: report.campaignEndDate?.split('T')[0] || '',
          });
        }
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(formData: any) {
    setSaving(true);
    setError(null);
    try {
      const { campaignName, brandId, reportDate, campaignStartDate, campaignEndDate, ...reportData } = formData;

      // Make all fields optional
      const payload: any = {
        brandId: brandId || null,
        campaignName: campaignName || null,
        reportType: 'mid-campaign' as const,
        campaignStartDate: campaignStartDate ? new Date(campaignStartDate).toISOString() : null,
        campaignEndDate: campaignEndDate ? new Date(campaignEndDate).toISOString() : null,
        reportDate: reportDate ? new Date(reportDate).toISOString() : null,
        reportData: JSON.stringify(reportData),
      };

      let reportId = id;
      if (!isEdit) {
        // Create new report
        const createRes = await fetch('/api/admin/reports', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        });

        if (!createRes.ok) {
          const error = await createRes.json();
          throw new Error(error.message || 'Failed to create report');
        }

        const createdReport = await createRes.json();
        reportId = createdReport.id;
        navigate(`/admin/reports/${reportId}/view`);
      } else {
        // Update existing report
        const updatePayload: any = {
          brandId: brandId || null,
          campaignName,
          campaignStartDate: campaignStartDate ? new Date(campaignStartDate).toISOString() : null,
          campaignEndDate: campaignEndDate ? new Date(campaignEndDate).toISOString() : null,
          reportDate: reportDate ? new Date(reportDate).toISOString() : null,
          reportData: JSON.stringify(reportData),
        };
        
        console.log('Updating report:', {
          reportId: id,
          reportDataLength: updatePayload.reportData?.length || 0,
          totalImpressions: reportData.totalImpressions,
        });
        
        const updateRes = await fetch(`/api/admin/reports/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(updatePayload),
        });

        if (!updateRes.ok) {
          const error = await updateRes.json();
          throw new Error(error.message || 'Failed to update report');
        }

        navigate(`/admin/reports/${id}/view`);
      }
    } catch (error: any) {
      console.error('Failed to save report:', error);
      setError(error.message || 'Failed to save report');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/reports')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold">{isEdit ? 'Edit Report' : 'Create New Report'}</h1>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <ManualReportForm
        reportId={id}
        brands={brands}
        initialData={initialData}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}

